import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { FileHandlerBaseService } from '@shared/services/file-handler/file-handler-base.service';
import {
  CUSTOMER_REPORTING_ACTIONS,
  CustomerReportingDataService,
  ICallerInformation,
  IReportFilters,
  ReportStoredProcedure,
} from '@usf/ngrx-customer-reporting';
import { first } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CustomerReportingCsvService {
  constructor(
    private fileHandlerBaseService: FileHandlerBaseService,
    private dataService: CustomerReportingDataService,
    private store: Store,
  ) {}

  downloadCsv(
    callerInfo: ICallerInformation,
    report: ReportStoredProcedure,
    filters: IReportFilters,
  ): void {
    this.store.dispatch(
      CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReport({ report }),
    );

    this.dataService
      .getCustomerReportCsv(callerInfo, report, filters)
      .pipe(first())
      .subscribe(response => {
        if (response.status === 204) {
          this.store.dispatch(
            CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportFailed({
              error: 'NO_DATA',
              report,
            }),
          );
        } else {
          const contentDisposition = response.headers.get(
            'Content-Disposition',
          );
          const filename =
            this.getFilenameFromContentDisposition(contentDisposition);

          // Process the Blob response as a stream
          const blob = response.body;
          const reader = blob.stream().getReader();
          const decoder = new TextDecoder('utf-8');
          let csvData = '';

          reader.read().then(
            function processText({ done, value }) {
              if (done) {
                this.generateDownload(csvData, filename, report); // Call generateDownload with the complete CSV data
                return;
              }

              csvData += decoder.decode(value, { stream: true });
              return reader.read().then(processText.bind(this)); // Bind this to ensure correct context
            }.bind(this),
          );
        }
      });
  }

  getFilenameFromContentDisposition(
    contentDisposition: string | null,
  ): string | null {
    if (!contentDisposition) return null;
    const matches = /filename="([^"]+)"/.exec(contentDisposition);
    return matches ? matches[1] : null;
  }

  async generateDownload(
    csvData: Blob,
    fileName: string,
    report: string,
  ): Promise<void> {
    const blob = new Blob([csvData], { type: 'text/csv' });

    try {
      await this.fileHandlerBaseService.downloadFile(
        blob,
        fileName,
        'text/csv',
      );
      this.store.dispatch(
        CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportSuccess({ report }),
      );
    } catch (error) {
      this.store.dispatch(
        CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportFailed({
          report: 'csv',
          error,
        }),
      );
    }
  }
}
